import * as _ from 'lodash';
import { getLogger } from '../diagnostics/logger';
import moment from 'moment';
import { msisdnSwapValueUpdate } from 'src/models/payload/msisdnSwapValueUpdate';
import {
  DESELECT_SIMS,
  LOAD_COMMAND_STOCKS_FAILURE,
  LOAD_COMMAND_STOCKS_PENDING,
  LOAD_COMMAND_STOCKS_SUCCESS,
  MSISDN_SWAP_TEL_CHANGED,
  SET_COMMAND_PROVISIONING,
  SIM_COMMAND_SET_DATE,
  SIM_COMMAND_SET_PO,
  SIM_COMMAND_SET_VALUE,
  SIM_PROVISION_ADD_TARIFF_BOLT_ON,
  SIM_PROVISION_CLEAR_NETWORK_TARIFF,
  SIM_PROVISION_REMOVE_TARIFF_BOLT_ON,
  SIM_PROVISION_SET_NETWORK_TARIFF,
  SIM_PROVISION_TEL_CHANGED,
  SIM_SUBMISSION_SET_ACTION_DATE,
  SIM_SUBMISSION_SET_ACTION_IMMEDIATELY,
  SIM_SUBMISSION_SET_NAME,
  SIM_SWAP_SERIAL_CHANGED,
  SIM_SWAP_SERIAL_CHANGED_ASYNC,
  CLEAR_SIM_SUBMISSION
} from '../actions/actionTypes';
import { Action, ActionMeta, PromiseAction } from '../actions/commonActions';
import { BoltOnUniqueItem, NonUniqueItem } from '../components/ui/input/DropdownInput';
import { HashMap } from '../constants';
import { SimManagementError } from '../models/Errors';
import { AddBoltOnPayload, LoadCommandStocksPayload, RemoveBoltOnPayload } from '../models/payload/loadRequests';
import { msisdnSwapChangePayload } from '../models/payload/msisdnSwapChangePayload';
import { simSwapValueUpdate } from '../models/payload/simSwapValueUpdate';
import { valueUpdate } from '../models/payload/valueUpdate';
import { Provisioning } from '../models/provision';
import { SimInfo } from '../models/sim';
import { SimCommandCreate } from '../models/simCommand';
import { initialAppState } from '../models/state/appState';
import { CommandTypesCacheTimeout } from '../models/state/directlyAvailableCommandTypeState';
import {
  ChangeProvisionCommand,
  EditPoCommand,
  MsisdnSwapCommand,
  ProvisionCommand,
  SimSwapCommand,
  SubmissionCommand
} from '../models/state/commands';
import { SubmissionState } from '../models/state/simProvision/simProvisionState';
import { commandService, getDefaultSubmissionState } from '../models/state/submissionDefaults';

const log = getLogger('simManagement.api.submissionReducer');

export default function submissionReducer(
  state: SubmissionState = initialAppState.submission,
  action: Action<any>
): SubmissionState {
  switch (action.type) {
    case LOAD_COMMAND_STOCKS_PENDING: {
      const { payload } = action as Action<LoadCommandStocksPayload>;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const newState: SubmissionState = {
        ...getDefaultSubmissionState(),
        isLoading: true,
        params: {
          ...payload
        }
      };

      return newState;
    }
    case LOAD_COMMAND_STOCKS_FAILURE: {
      const { meta, payload } = action as ActionMeta<SimManagementError, LoadCommandStocksPayload>;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const newState: SubmissionState = {
        ...getDefaultSubmissionState(),
        error: payload,
        params: {
          ...meta
        }
      };

      return newState;
    }
    case LOAD_COMMAND_STOCKS_SUCCESS: {
      const { meta, payload } = action as ActionMeta<HashMap<SimCommandCreate>, LoadCommandStocksPayload>;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      // convert into a more useful format for displaying the tabs
      const tabs: SubmissionCommand[] = [];
      for (const commandId in payload) {
        if (payload.hasOwnProperty(commandId)) {
          const simCommand = payload[commandId] as SimCommandCreate;
          const tab = commandService.createCommandEntryTab(simCommand);
          tab.stockIds = [...simCommand.stockIds];

          tabs.push(tab);
        }
      }

      const changedState: Partial<SubmissionState> = {
        params: meta,
        simCommands: tabs,
        isLoading: false,
        expiresOn: moment().add(CommandTypesCacheTimeout, 'seconds').toDate(),
        lastLoadedOn: state.lastLoadedOn || new Date(),
        simDefinitionAliases: tabs.map((t) => t.key /* t.definition.jsonAlias*/) /* todo: obsolete? */
      };

      const newState = Object.assign({}, state, changedState);
      return newState;
    }
    case SIM_PROVISION_CLEAR_NETWORK_TARIFF: {
      const typedAction = action as Action<string>;
      if (!typedAction.payload) {
        return state;
      }

      const commandKey = typedAction.payload;
      const newState = setNetworkTariff(commandKey, undefined);
      return newState;
    }
    case SIM_PROVISION_SET_NETWORK_TARIFF: {
      const typedAction = action as ActionMeta<string, number>;
      if (!typedAction.payload) {
        log.warn('No payload');
        return state;
      }

      if (!typedAction.meta) {
        log.warn('No meta');
        return state;
      }

      const commandKey = typedAction.payload;
      const tariffNumber = typedAction.meta;
      const newState = setNetworkTariff(commandKey, tariffNumber);
      return newState;
    }
    case SIM_COMMAND_SET_DATE: {
      const typedPayload = action as ActionMeta<string, SimCommandPropertyUpdate<Date>>;
      const { payload, meta } = typedPayload;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const { name, value } = meta;
      if (!value || !_.isDate(value) || isNaN(value.getDate())) {
        log.warn('No valid meta');
        return state;
      }

      const commandDefinitionAlias = payload;
      const changedCommandState: any = {};
      changedCommandState[name] = value;

      const newState = getNewState(commandDefinitionAlias, changedCommandState);
      return newState;
    }
    case SIM_COMMAND_SET_VALUE: {
      const typedPayload = action as ActionMeta<string, valueUpdate>;
      const { payload, meta } = typedPayload;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      if (!meta) {
        log.warn('No meta');
        return state;
      }

      const commandKey = payload;
      const changedCommandState: any = {};
      if (meta.subName) {
        const command = state.simCommands.find((c: any) => c.key === commandKey);
        if (!command) {
          return state;
        }

        const propertyValue = (command as any)[meta.name];
        if (!propertyValue) {
          return state;
        }

        changedCommandState[meta.name] = Object.assign({}, propertyValue, {
          [meta.subName]: meta.value
        });
      } else {
        changedCommandState[meta.name] = meta.value;
      }

      const newState = getNewState(commandKey, changedCommandState);
      return newState;
    }
    case SIM_PROVISION_TEL_CHANGED: {
      const typedPayload = action as ActionMeta<string, simSwapValueUpdate>;
      const { payload, meta } = typedPayload;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      if (!meta) {
        log.warn('No meta');
        return state;
      }

      const key = payload;
      const { data, oldValue, newValue } = meta;
      const command = state.simCommands.find((c: any) => c.key === key) as ProvisionCommand;
      const index = command.stockIds.indexOf(data.stockId);
      const tels = [...command.tels];
      tels[index] = newValue;
      const changedCommandState: Partial<ProvisionCommand> = {
        tels
      };

      const newState = getNewState(key, changedCommandState);
      return newState;
    }
    case MSISDN_SWAP_TEL_CHANGED: {
      const typedPayload = action as ActionMeta<string, msisdnSwapValueUpdate>;
      const { payload, meta } = typedPayload;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      if (!meta) {
        log.warn('No meta');
        return state;
      }

      const commandDefinitionAlias = payload;
      const { data, newValue } = meta;
      const command = state.simCommands.find(
        (c: any) => c.key === commandDefinitionAlias
      ) as MsisdnSwapCommand;
      const index = command.stockIds.indexOf(data.stockId);
      const swaps = [...command.swaps];
      swaps[index] = newValue;
      const changedCommandState: Partial<MsisdnSwapCommand> = {
        swaps
      };

      const newState = getNewState(commandDefinitionAlias, changedCommandState);
      return newState;
    }
    case SIM_SWAP_SERIAL_CHANGED: {
      const typedPayload = action as ActionMeta<string, simSwapValueUpdate>;
      const { payload, meta } = typedPayload;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      if (!meta) {
        log.warn('No meta');
        return state;
      }

      const commandDefinitionAlias = payload;
      const { data, newValue } = meta;
      const command = state.simCommands.find(
        (c: any) => c.key === commandDefinitionAlias
      ) as SimSwapCommand;
      const index = command.stockIds.indexOf(data.stockId);
      const simSwaps = [...command.simSwaps];
      simSwaps[index] = newValue;
      const changedCommandState: Partial<SimSwapCommand> = {
        simSwaps
      };

      const newState = getNewState(commandDefinitionAlias, changedCommandState);
      return newState;
    }
    case SIM_SWAP_SERIAL_CHANGED_ASYNC: {
      const typedPayload = action as PromiseAction<msisdnSwapChangePayload, simSwapValueUpdate>;
      const { payload } = typedPayload;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const commandDefinitionAlias = payload.commandDefinition;
      const { data, newValue } = payload.value;
      const command = state.simCommands.find(
        (c: any) => c.key === commandDefinitionAlias
      ) as SimSwapCommand;
      const index = command.stockIds.indexOf(data.stockId);
      const simSwaps = [...command.simSwaps];
      simSwaps[index] = newValue;
      const changedCommandState: Partial<SimSwapCommand> = {
        simSwaps
      };

      const newState = getNewState(commandDefinitionAlias, changedCommandState);
      return newState;
    }
    case SIM_COMMAND_SET_PO: {
      const { payload, meta } = action as ActionMeta<string, string>;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      if (!meta) {
        log.warn('No meta');
        return state;
      }

      const commandKey = payload;
      const po = meta;
      if (commandKey !== 'edit-po') {
        log.warn(`Unexpected command key: ${commandKey}`);
        return state;
      }

      const updatedSimCommands: SubmissionCommand[] = [];
      state.simCommands.forEach((value: any, index: any, array: any) => {
        if (value.key === commandKey) {
          const currentCommand = value as EditPoCommand;
          const poArray = po.split(',').map((x) => (x === 'null' ? null : x));
          const normalizedPoNumber = (poValue: string | null | undefined): string | null => {
            if (poValue == null) {
              return null;
            }

            return poValue.trim();
          };

          updatedSimCommands.push({
            ...currentCommand,
            // Preserve explicit nulls for unset fields so backend field ordering remains stable.
            poNumber1: normalizedPoNumber(poArray[0]),
            poNumber2: normalizedPoNumber(poArray[1]),
            poNumber3: normalizedPoNumber(poArray[2]),
            poNumber4: normalizedPoNumber(poArray[3]),
            poNumber5: normalizedPoNumber(poArray[4])
          });
        } else {
          updatedSimCommands.push(value);
        }
      });

      const newState = Object.assign({}, state, {
        simCommands: updatedSimCommands
      });

      return newState;
    }
    case SET_COMMAND_PROVISIONING: {
      const { payload, meta } = action as ActionMeta<string, Provisioning>;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      if (!meta) {
        log.warn('No meta');
        return state;
      }

      const commandKey = payload;
      const provisioning = meta;
      const updatedSimCommands: SubmissionCommand[] = [];
      state.simCommands.forEach((value: any, index: any, array: any) => {
        if (value.key === commandKey) {
          const currentCommand = value as ProvisionCommand | ChangeProvisionCommand;
          const changedCommandState: Partial<ProvisionCommand | ChangeProvisionCommand> = {
            provisioningOrderNumber: provisioning.provisioningOrder && provisioning.provisioningOrder.number,
            tariffNumber: provisioning.tariff && provisioning.tariff.number,
            boltOns: provisioning.standardBoltOns.map((boltOn) => {
              let i = 1;
              let uniqueKey: React.Key = boltOn.number + '_' + i;
              while (currentCommand.boltOns.find((b) => b.uniqueKey === uniqueKey)) {
                i++;
                uniqueKey = boltOn.number + '_' + i;
              }

              const item: BoltOnUniqueItem = {
                key: boltOn.number,
                uniqueKey,
                label: boltOn.name,
                type: 'item',
                canDelete: !boltOn.isExisting || !boltOn.isTemporal,
                isDataBoltOn: boltOn.isDataBoltOn,
                isLegacy: boltOn.isLegacy,
                domesticVoice: boltOn.domesticVoice,
                internationalVoice: boltOn.internationalVoice,
                publicIp: boltOn.publicIp,
                fixedIp: boltOn.fixedIp
              };

              return item;
            })
          };

          if (currentCommand.definitionAlias === 'change-provision') {
            (changedCommandState as Partial<ChangeProvisionCommand>).temporalBoltOns = provisioning.temporalBoltOns.map(
              (boltOn) => {
                let i = 1;
                let uniqueKey: React.Key = boltOn.number + '_' + i;
                while (currentCommand.temporalBoltOns.find((b) => b.uniqueKey === uniqueKey)) {
                  i++;
                  uniqueKey = boltOn.number + '_' + i;
                }

                const item: BoltOnUniqueItem = {
                  key: boltOn.number,
                  uniqueKey,
                  label: boltOn.name,
                  type: 'item',
                  canDelete: !boltOn.isExisting || !boltOn.isTemporal,
                  isDataBoltOn: boltOn.isDataBoltOn,
                  isLegacy: boltOn.isLegacy,
                  domesticVoice: boltOn.domesticVoice,
                  internationalVoice: boltOn.internationalVoice
                };

                return item;
              }
            );
          }

          const updatedCommand: ProvisionCommand | ChangeProvisionCommand = Object.assign(
            {},
            currentCommand,
            changedCommandState
          );
          if (updatedCommand.definitionAlias === 'change-provision') {
            updatedCommand.boltOnsString = updatedCommand.boltOns
              .concat(updatedCommand.temporalBoltOns)
              .map((b) => b.key)
              .join(',');
          } else {
            updatedCommand.boltOnsString = updatedCommand.boltOns.map((b) => b.key).join(',');
          }
          updatedSimCommands.push(updatedCommand);
        } else {
          updatedSimCommands.push(value);
        }
      });

      const changedState: Partial<SubmissionState> = {
        simCommands: updatedSimCommands
      };

      const newState = Object.assign({}, state, changedState);
      return newState;
    }
    case SIM_PROVISION_ADD_TARIFF_BOLT_ON: {
      const { payload } = action as Action<AddBoltOnPayload>;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const key = payload.commandKey;
      const updatedSimCommands: SubmissionCommand[] = [];
      state.simCommands.forEach((value: any, index: any, array: any) => {
        if (value.key === key) {
          const currentCommand = value as ProvisionCommand | ChangeProvisionCommand;
          let i = 0;
          let uniqueKey: React.Key = payload.boltOn.key + '_' + i;
          const currentBoltOns =
            payload.isTemporal && currentCommand.definitionAlias === 'change-provision'
              ? currentCommand.temporalBoltOns
              : currentCommand.boltOns;
          while (currentBoltOns.find((b) => b.uniqueKey === uniqueKey)) {
            i++;
            uniqueKey = payload.boltOn.key + '_' + i;
          }

          const boltOn: BoltOnUniqueItem = {
            key: payload.boltOn.key,
            label: payload.boltOn.label,
            isDataBoltOn: payload.boltOn.isDataBoltOn,
            uniqueKey,
            canDelete: true,
            isLegacy: payload.boltOn.isLegacy,
            domesticVoice: payload.boltOn.domesticVoice,
            internationalVoice: payload.boltOn.internationalVoice,
            publicIp: payload.boltOn.publicIp,
            fixedIp: payload.boltOn.fixedIp
          };

          const updatedCommand = getCommandChangesOnBoltOnAddition(currentCommand, payload, boltOn);
          updatedSimCommands.push(updatedCommand);
        } else {
          updatedSimCommands.push(value);
        }
      });

      const changedState: Partial<SubmissionState> = {
        simCommands: updatedSimCommands
      };

      const newState = Object.assign({}, state, changedState);
      return newState;
    }
    case SIM_PROVISION_REMOVE_TARIFF_BOLT_ON: {
      const { payload } = action as Action<RemoveBoltOnPayload>;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const key = payload.commandKey;
      const boltOn = payload.boltOn;

      const updatedSimCommands: SubmissionCommand[] = [];
      state.simCommands.forEach((command: any) => {
        if (command.key === key) {
          const currentCommand = command as ProvisionCommand | ChangeProvisionCommand;
          const updatedCommand = getCommandChangesOnBoltOnRemoval(currentCommand, payload, boltOn);
          updatedSimCommands.push(updatedCommand);
        } else {
          updatedSimCommands.push(command);
        }
      });

      const changedState: Partial<SubmissionState> = {
        simCommands: updatedSimCommands
      };

      const newState = Object.assign({}, state, changedState);
      return newState;
    }
    case SIM_SUBMISSION_SET_ACTION_DATE: {
      const typedPayload = action as ActionMeta<string, Date>;
      const { payload, meta } = typedPayload;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const actionDate = meta;
      if (!actionDate || !_.isDate(actionDate) || isNaN(actionDate.getDate())) {
        log.warn('No valid meta');
        return state;
      }

      const changedState: Partial<SubmissionState> = {
        actionDate
      };

      const newState = Object.assign({}, state, changedState);
      return newState;
    }
    case SIM_SUBMISSION_SET_ACTION_IMMEDIATELY: {
      const typedPayload = action as ActionMeta<string, boolean>;
      const { payload, meta } = typedPayload;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      if (_.isNull(meta) || _.isUndefined(meta)) {
        log.warn('No meta');
        return state;
      }

      const delayAction = meta;
      const changedState: Partial<SubmissionState> = {
        delayAction
      };

      const newState = Object.assign({}, state, changedState);
      return newState;
    }
    case SIM_SUBMISSION_SET_NAME: {
      const typedPayload = action as ActionMeta<string, string>;
      const { payload, meta } = typedPayload;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      if (_.isNull(meta) || _.isUndefined(meta)) {
        log.warn('No meta');
        return state;
      }

      const name = meta;
      const changedState: Partial<SubmissionState> = {
        name
      };

      const newState = Object.assign({}, state, changedState);
      return newState;
    }
    case DESELECT_SIMS: {
      const typedAction = action as Action<SimInfo[]>;
      const { payload } = typedAction;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const simsById = Object.assign({}, ...payload.map((a) => ({ [a.stockId]: a })));
      const simsBySerialNumber = Object.assign({}, ...payload.map((a) => ({ [a.serialNumber]: a })));
      // remove SIM from the relevant tab & remove tab if it no longer has any selected stocks
      const simCommands = state.simCommands
        .map((cmd: any, index: any, array: any) => {
          const stockIds = cmd.stockIds.filter((stockId: any) => {
            return !simsById[stockId];
          });

          if (stockIds.length > 0) {
            let cmdChanges: Partial<SubmissionCommand>;
            if (cmd.definitionAlias === 'sim-swap') {
              const simSwaps = cmd.simSwaps.filter((swap: any, swapIndex: any) => {
                const associatedStockId = cmd.stockIds[swapIndex];
                return !simsById[associatedStockId];
              });

              const simSwapCommand: Partial<SimSwapCommand> = {
                simSwaps,
                stockIds
              };
              cmdChanges = simSwapCommand;
            } else {
              cmdChanges = { stockIds };
            }

            const tab = commandService.createCommandEntryTab({
              jsonAlias: cmd.definitionAlias,
              networkId: cmd.networkId,
              accountId: cmd.accountId,
              stockIds: cmd.stockIds,
              tabName: cmd.definition.tabName
            });

            return Object.assign(tab, cmd, cmdChanges);
          } else {
            return cmd;
          }
        })
        .filter((cmd: any) => cmd.stockIds.length > 0);

      const selectedStockSerialNumbers = state.params.selectedStockSerialNumbers.filter(
        (serialNumber: any) => !!simsBySerialNumber[serialNumber]
      );
      const changedState: Partial<SubmissionState> = {
        simCommands,
        params: {
          commandType: state.params.commandType,
          selectedStockSerialNumbers
        }
      };

      const newState = Object.assign({}, state, changedState);
      return newState;
    }
    case CLEAR_SIM_SUBMISSION: {
      return getDefaultSubmissionState();
    }
    default:
      return state;
  }

  function getCommandChangesOnBoltOnAddition(
    currentCommand: ChangeProvisionCommand | ProvisionCommand,
    payload: AddBoltOnPayload,
    boltOn: BoltOnUniqueItem
  ): ChangeProvisionCommand | ProvisionCommand {
    if (currentCommand.definitionAlias === 'change-provision') {
      const commandChanges: Partial<ChangeProvisionCommand> = payload.isTemporal
        ? {
            temporalBoltOns: [...currentCommand.temporalBoltOns, boltOn]
          }
        : {
            boltOns: [...currentCommand.boltOns, boltOn],
            temporalBoltOns: []
          };

      const updatedCommand: ChangeProvisionCommand = Object.assign({}, currentCommand, commandChanges);
      updatedCommand.boltOnsString = updatedCommand.boltOns
        .concat(updatedCommand.temporalBoltOns)
        .map((b) => b.key)
        .join(',');
      return updatedCommand;
    } else {
      const boltOns = [...currentCommand.boltOns, boltOn];

      const commandChanges: Partial<ProvisionCommand> = {
        boltOns,
        boltOnsString: boltOns.map((b) => b.key).join(',')
      };

      const updatedCommand: ProvisionCommand = Object.assign({}, currentCommand, commandChanges);
      return updatedCommand;
    }
  }

  function getCommandChangesOnBoltOnRemoval(
    currentCommand: ChangeProvisionCommand | ProvisionCommand,
    payload: RemoveBoltOnPayload,
    boltOn: NonUniqueItem
  ): ChangeProvisionCommand | ProvisionCommand {
    if (currentCommand.definitionAlias === 'change-provision') {
      const commandChanges: Partial<ChangeProvisionCommand> = payload.isTemporal
        ? {
            temporalBoltOns: [
              ...currentCommand.temporalBoltOns.filter((value) => {
                return value.uniqueKey !== boltOn.uniqueKey;
              })
            ]
          }
        : {
            boltOns: [
              ...currentCommand.boltOns.filter((value) => {
                return value.uniqueKey !== boltOn.uniqueKey;
              })
            ]
          };

      const updatedCommand: ChangeProvisionCommand = Object.assign({}, currentCommand, commandChanges);
      updatedCommand.boltOnsString = updatedCommand.boltOns
        .concat(updatedCommand.temporalBoltOns)
        .map((b) => b.key)
        .join(',');
      return updatedCommand;
    } else {
      const boltOns = [
        ...currentCommand.boltOns.filter((value, index, array) => {
          return value.uniqueKey !== boltOn.uniqueKey;
        })
      ];

      const commandChanges: Partial<ProvisionCommand> = {
        boltOns,
        boltOnsString: boltOns.map((b) => b.key).join(',')
      };

      const updatedCommand: ProvisionCommand = Object.assign({}, currentCommand, commandChanges);
      return updatedCommand;
    }
  }

  function setNetworkTariff(commandKey: string, tariffNumber: number | undefined) {
    const changedCommandState: Partial<ProvisionCommand | ChangeProvisionCommand> = {
      tariffNumber,
      boltOns: [],
      boltOnsString: '',
      provisioningOrderNumber: undefined
    };

    const cmd = state.simCommands.find((c: any) => c.key === commandKey);
    if (cmd && cmd.definitionAlias === 'change-provision') {
      (changedCommandState as Partial<ChangeProvisionCommand>).temporalBoltOns = [];
    }

    const newState = getNewState(commandKey, changedCommandState);
    return newState;
  }

  function getNewState(commandKey: string, changedCommandState: Partial<SubmissionCommand>) {
    const updatedSimCommands: SubmissionCommand[] = [];
    state.simCommands.forEach((value: any, index: any, array: any) => {
      if (value.key === commandKey) {
        const tab = commandService.createCommandEntryTab({
          jsonAlias: value.definitionAlias,
          accountId: value.accountId,
          networkId: value.networkId,
          stockIds: value.stockIds,
          tabName: value.definition.tabName
        });

        if (tab.definitionAlias === 'provision') {
          const changedCommand = changedCommandState as Partial<ProvisionCommand>;
          if (
            changedCommand.provisionType &&
            changedCommand.provisionType !== (value as ProvisionCommand).provisionType
          ) {
            changedCommand.tels = Array(tab.stockIds.length) as (string | undefined)[];
          }
        }

        const updatedCommand = Object.assign(tab, value, changedCommandState);
        updatedSimCommands.push(updatedCommand);
      } else {
        updatedSimCommands.push(value);
      }
    });

    const changedState: Partial<SubmissionState> = {
      simCommands: updatedSimCommands
    };

    const newState = Object.assign({}, state, changedState);
    return newState;
  }
}
